
import React, { useState, useEffect } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from 'recharts';
import AnimatedNumber from './AnimatedNumber';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const CostComparison = () => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  const [animatedData, setAnimatedData] = useState([
    { name: 'Enterprise Solutions', cost: 0, color: '#F1F0FB' },
    { name: 'planniFi', cost: 0, color: '#27b5fc' }
  ]);

  // Updated monthly cost for planniFi to $247 (was $97)
  // The proportionality of the bar will be handled by recharts since the data now sets planniFi to 247 and competitor to 624.
  const finalData = [
    { name: 'Enterprise Solutions', cost: 624, color: '#F1F0FB' },
    { name: 'planniFi', cost: 247, color: '#27b5fc' }
  ];

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        setAnimatedData(finalData);
      }, 500);
    }
  }, [isVisible]);

  return (
    <div className="bg-white rounded-xl p-8 h-full flex flex-col">
      <h2 
        className={`text-3xl md:text-4xl font-charter mb-6 text-center transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        Unmatched savings.
      </h2>
      
      <div 
        ref={ref} 
        className={`transition-opacity duration-1000 flex-1 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transition: 'all 1.5s ease-out' }}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={animatedData}
            margin={{ top: 20, right: 30, left: 30, bottom: 40 }}
          >
            <XAxis 
              type="category" 
              dataKey="name" 
              tick={{ fontSize: 14 }}
              axisLine={false}
            />
            <Bar 
              dataKey="cost" 
              fill="#27b5fc"
              radius={[4, 4, 0, 0]}
              label={({ value, x, y, width }) => (
                <text
                  x={x + width / 2}
                  y={y - 20}
                  fill="#888888"
                  textAnchor="middle"
                  fontSize={14}
                  className="font-medium"
                >
                  ${value}
                  /mo
                </text>
              )}
            >
              {animatedData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        
        <div className="text-center text-secondary mt-2 whitespace-nowrap text-xs sm:text-sm md:text-base">
          Save up to $<AnimatedNumber end={4524} duration={1500} /> per year with planniFi
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-[1em] w-[1em] text-secondary cursor-pointer ml-2 inline-block align-middle" />
              </TooltipTrigger>
              <TooltipContent className="bg-white border rounded-lg p-3 max-w-[160px] text-xs leading-relaxed whitespace-normal">
                The estimated savings is based on an average of comparable media planning and budgeting services.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
};

export default CostComparison;

